/**
 * WebSocket hook for real-time file processing updates.
 *
 * Connects to the backend WebSocket endpoint to receive live progress updates
 * during file processing (uploading, parsing, chunking, embedding, etc.)
 */

import { useEffect, useRef, useCallback } from 'react';
import { config } from '../config';
import { useAuthStore } from '../stores/authStore';

export interface FileProcessingUpdate {
  type: 'file_processing_update';
  file_id: string;
  status: 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  message: string;
  timestamp: string;
  metadata?: {
    filename?: string;
    size_bytes?: number;
    title?: string;
    author?: string;
    page_count?: number;
    word_count?: number;
    chunk_count?: number;
    document_count?: number;
    chunks_created?: number;
    embedding_count?: number;
    document_ids?: string[];
    error?: string;
    error_type?: string;
  };
}

interface UseFileProcessingWebSocketOptions {
  onUpdate?: (update: FileProcessingUpdate) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  autoReconnect?: boolean;
  reconnectDelay?: number;
}

/**
 * Hook to establish WebSocket connection for file processing updates.
 *
 * @param options Configuration options
 * @returns Connection state and manual control functions
 *
 * @example
 * ```tsx
 * const { isConnected, disconnect } = useFileProcessingWebSocket({
 *   onUpdate: (update) => {
 *     console.log(`File ${update.file_id}: ${update.message} (${update.progress}%)`);
 *     // Update UI with progress
 *   },
 *   onError: (error) => console.error('WebSocket error:', error),
 *   autoReconnect: true
 * });
 * ```
 */
export function useFileProcessingWebSocket(options: UseFileProcessingWebSocketOptions = {}) {
  const {
    onUpdate,
    onError,
    onClose,
    autoReconnect = true,
    reconnectDelay = 3000
  } = options;

  const accessToken = localStorage.getItem('access_token');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isManualDisconnect = useRef(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Use refs for callbacks to avoid triggering reconnects on every render
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);

  // Update refs when callbacks change
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onErrorRef.current = onError;
    onCloseRef.current = onClose;
  }, [onUpdate, onError, onClose]);

  const connect = useCallback(() => {
    // Don't connect if no access token (silently return - this is normal on initial load)
    if (!accessToken) {
      return;
    }

    // Don't reconnect if manually disconnected
    if (isManualDisconnect.current) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // wsBaseUrl already includes /api/ws prefix (from .env: wss://...com/api/ws)
      const url = `${config.wsBaseUrl}/clipboard?token=${encodeURIComponent(accessToken)}`;

      const ws = new WebSocket(url);

      ws.onopen = () => {
        reconnectAttempts.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle file processing updates
          if (data.type === 'file_processing_update' && onUpdateRef.current) {
            onUpdateRef.current(data as FileProcessingUpdate);
          }

          // Respond to ping messages
          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      ws.onerror = (error) => {
        if (onErrorRef.current) {
          onErrorRef.current(error);
        }
      };

      ws.onclose = () => {
        wsRef.current = null;

        if (onCloseRef.current) {
          onCloseRef.current();
        }

        // Auto-reconnect if enabled, not manually disconnected, and under max attempts
        if (autoReconnect && !isManualDisconnect.current) {
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            // Exponential backoff: 3s, 6s, 12s, 24s, 48s
            const delay = reconnectDelay * Math.pow(2, reconnectAttempts.current - 1);
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          }
        }
      };

      wsRef.current = ws;
    } catch (error) {
      if (onErrorRef.current) {
        onErrorRef.current(error as Event);
      }
    }
  }, [accessToken, autoReconnect, reconnectDelay]);

  const disconnect = useCallback(() => {
    isManualDisconnect.current = true;

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Connect on mount and when access token changes, disconnect on unmount
  useEffect(() => {
    isManualDisconnect.current = false;
    connect();

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    disconnect,
    reconnect: () => {
      isManualDisconnect.current = false;
      connect();
    }
  };
}
